import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Card,
  Input,
  InputNumber,
  Button,
  Spin,
  message,
  Switch,
} from "antd";
import { SearchOutlined, RiseOutlined, HomeOutlined } from "@ant-design/icons";
import { getApiUrl } from "../lib/api/config";
import { getAuth } from "firebase/auth";
import styled from "styled-components";
import PageHeader from "../components/PageHeader";
import { LAYOUT_CONSTANTS } from "../lib/breakpoints";

const PageContainer = styled.div`
  padding: 2rem;
  background: var(--color-bg-primary);
  min-height: 100vh;
  position: relative;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-bottom: 5rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
  }
`;

const GuideSection = styled.section`
  width: 100%;
  max-width: 720px;
  margin: 0 auto 1.5rem;
  padding: 1.35rem 1.5rem 1.35rem;
  text-align: left;
  box-sizing: border-box;
  background: linear-gradient(
    180deg,
    rgba(44, 95, 45, 0.06) 0%,
    #ffffff 55%
  );
  border: 1px solid rgba(44, 95, 45, 0.12);
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

  @media (min-width: 769px) {
    max-width: min(1100px, 95%);
  }

  @media (max-width: 768px) {
    padding: 1.1rem 1rem;
    margin-bottom: 1.25rem;
  }
`;

const GuideTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-text, #1f2937);
  font-family: var(--font-primary, inherit);
  letter-spacing: -0.02em;
`;

/** One typography context so the intro paragraph and bullets share the exact same font size. */
const GuideBody = styled.div`
  font-size: 0.875rem;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.78);
  font-family: var(--font-secondary, inherit);

  p,
  ul,
  li {
    font-size: inherit;
    line-height: inherit;
    font-family: inherit;
    color: inherit;
  }
`;

const GuideLead = styled.p`
  margin: 0 0 0.75rem;
`;

const GuideBullets = styled.ul`
  margin: 0;
  padding: 0 0 0 1.35rem;
  list-style-type: disc;
  list-style-position: outside;

  li {
    margin: 0 0 0.45rem 0;
    padding-left: 0.35rem;
    display: list-item;
  }

  li::marker {
    color: rgba(44, 95, 45, 0.8);
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

const ContentCard = styled(Card)`
  max-width: 720px;
  margin: 0 auto;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  .ant-card-body {
    padding: 1.5rem 2rem;
  }
  .ant-btn-primary .anticon {
    color: #fff !important;
  }
  @media (min-width: 769px) {
    max-width: min(1100px, 95%);
  }
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 1rem;
    }
  }
`;

const InputWrapper = styled.div`
  flex: 1;
  min-width: 0;
  border-radius: 6px;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  &:focus-within {
    border-color: var(--color-primary, #2c5f2d);
    outline: none;
  }

  .ant-input {
    border: none !important;
    box-shadow: none !important;
    outline: none;
    height: 40px;
    padding: 4px 11px;
  }

  .ant-input:focus {
    border: none !important;
    box-shadow: none !important;
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 0;
  flex-wrap: wrap;
  align-items: stretch;

  .ant-btn {
    height: auto;
    padding: 4px 15px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    .ant-btn {
      width: 100%;
      min-height: 48px;
      padding: 12px 16px;
    }
  }
`;

const StrToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 1rem;
  flex-wrap: wrap;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  text-align: center;
  .ant-switch {
    flex-shrink: 0;
  }
`;

const StrToggleHint = styled.span`
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
  width: 100%;
  text-align: center;
  @media (min-width: 769px) {
    width: auto;
    margin-left: 0.25rem;
  }
`;

const StrYieldSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #f0f5ff 0%, #e6f4ff 100%);
  border: 1px solid #91caff;
  border-radius: 12px;
  grid-column: 1 / -1;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding: 1.25rem 1rem;
    text-align: center;
  }
`;

const StrYieldTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1677ff;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    font-size: 1rem;
    margin-bottom: 1rem;
    gap: 0.35rem;
  }
`;

const StrYieldTitleMain = styled.span`
  @media (max-width: 768px) {
    display: block;
  }
`;

const StrYieldTitleSub = styled.span`
  @media (max-width: 768px) {
    display: block;
    font-size: 0.95rem;
  }
`;

const StrYieldTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const StrYieldBlock = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  padding: 1.25rem;
  border: 1px solid rgba(22, 119, 255, 0.15);
  min-width: 0;

  @media (max-width: 768px) {
    padding: 1rem;
    text-align: center;
  }
`;

const StrYieldBlockTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const StrYieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.5rem 0;
  gap: 0.75rem;
  font-size: 0.95rem;
  min-width: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.25rem;
    font-size: 0.9rem;
    justify-content: center;
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const StrYieldLabel = styled.span`
  color: rgba(0, 0, 0, 0.65);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`;

const StrYieldValue = styled.span`
  font-weight: 600;
  color: #1677ff;
  text-align: right;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`;

const StrNightlyRateInput = styled(InputNumber)`
  max-width: 120px;

  .ant-input-number-input {
    font-weight: 600;
    color: #1677ff;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const StrDisclaimer = styled.p`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
  margin: 0.5rem 0 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const ScenarioPanel = styled.div`
  margin: 0 0 1.25rem;
  padding: 1.2rem 1.35rem;
  text-align: left;
  background: rgba(44, 95, 45, 0.05);
  border: 1px solid rgba(44, 95, 45, 0.14);
  border-radius: 12px;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ScenarioHeadRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.6rem;
  margin-bottom: 0.65rem;
`;

const ScenarioPanelTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-primary-darker, #1e4620);
  font-family: var(--font-primary, inherit);
`;

const ScenarioResetButton = styled.button`
  margin: 0;
  padding: 0.12rem 0.4rem;
  font-size: 0.65rem;
  line-height: 1.25;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: rgba(0, 0, 0, 0.75);
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 5px;
  cursor: pointer;
  font-family: var(--font-secondary, inherit);
  vertical-align: middle;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: #fff;
    border-color: rgba(44, 95, 45, 0.35);
    color: var(--color-primary-darker, #1e4620);
  }

  &:focus-visible {
    outline: 2px solid rgba(44, 95, 45, 0.45);
    outline-offset: 1px;
  }
`;

const ScenarioPanelHint = styled.div`
  margin: 0 0 0.85rem;
  max-width: 40rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.55);
  font-family: var(--font-secondary, inherit);

  p {
    margin: 0 0 0.35rem;
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const ScenarioGrid = styled.div`
  display: grid;
  gap: 0.85rem 1.25rem;
  grid-template-columns: 1fr 1fr;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ScenarioField = styled.div`
  min-width: 0;

  .scenario-field-label {
    display: block;
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    color: rgba(0, 0, 0, 0.72);
    margin-bottom: 0.4rem;
  }
`;

/** One border around $ + input (no nested bordered inner field). */
const ScenarioMoneyInputGroup = styled.div`
  display: flex;
  align-items: stretch;
  min-width: 0;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: #4096ff;
  }

  &:focus-within {
    border-color: #4096ff;
    box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.08);
  }
`;

const ScenarioMoneyPrefix = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 0 0 11px;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
  font-variant-numeric: tabular-nums;
  user-select: none;
`;

const ScenarioInput = styled(InputNumber)`
  flex: 1;
  min-width: 0;
  width: 100% !important;

  &.ant-input-number {
    border: none !important;
    box-shadow: none !important;
    background: transparent;
  }

  &.ant-input-number-focused {
    box-shadow: none !important;
  }

  .ant-input-number-input-wrap {
    border: none !important;
  }

  .ant-input-number-handler-wrap {
    display: none;
  }

  .ant-input-number-input {
    font-variant-numeric: tabular-nums;
    text-align: left;
  }
`;

/** Offset scroll-into-view so the verdict banner clears the fixed NavBar (see colors.css --navbar-height). */
const ResultScrollAnchor = styled.div`
  scroll-margin-top: calc(var(--navbar-height, 70px) + 12px);

  @media (max-width: 768px) {
    scroll-margin-top: calc(var(--navbar-height-mobile, 60px) + 12px);
  }
`;

const ResultCard = styled(Card)`
  max-width: 720px;
  margin: 1.5rem auto 0;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  .ant-card-body {
    padding: 0;
  }
  @media (min-width: 769px) {
    max-width: min(1100px, 95%);
  }
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 0;
    }
  }
`;

const VerdictBanner = styled.div`
  padding: 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  border-radius: 20px 20px 0 0;
  background: ${(p) =>
    p.$incomplete
      ? "linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)"
      : p.$pass
      ? "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)"
      : "linear-gradient(135deg, #fff2f0 0%, #ffccc7 100%)"};
  border-bottom: 3px solid
    ${(p) => (p.$incomplete ? "#d4b106" : p.$pass ? "#52c41a" : "#ff4d4f")};
  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const VerdictIcon = styled.span`
  font-size: 2.5rem;
  line-height: 1;
  color: ${(p) =>
    p.$incomplete ? "#d4b106" : p.$pass ? "#52c41a" : "#ff4d4f"};
  margin-bottom: 0.25rem;
`;

const VerdictText = styled.div`
  width: 100%;
  text-align: center;
`;

const VerdictTitle = styled.div`
  font-size: 1.35rem;
  font-weight: 700;
  color: ${(p) =>
    p.$incomplete ? "#ad8b00" : p.$pass ? "#389e0d" : "#cf1322"};
  margin-bottom: 0.25rem;
`;

const VerdictSub = styled.div`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.55);
`;

const MetricsSection = styled.div`
  padding: 1.5rem 2rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: 768px) {
    padding: 1rem;
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const DesktopTwoCol = styled.div`
  display: contents;
`;

const DesktopCol = styled.div`
  min-width: 0;
`;

const DataSourceNote = styled.div`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 1rem;
  font-style: italic;
`;

const ListingLinkSection = styled.div`
  padding: 1rem 2rem;
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  min-width: 0;
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ListingLinkLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 0.35rem;
`;

const ListingLink = styled.a`
  font-size: 0.95rem;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1890ff !important;
  text-decoration: underline;
  cursor: pointer;
  &:hover,
  &:visited,
  &:focus {
    color: #1890ff !important;
  }
  &:hover {
    color: #40a9ff !important;
  }
`;

const FurtherDisclaimer = styled.p`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
  margin: 0 0 1rem;
  line-height: 1.4;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled.span`
  color: rgba(0, 0, 0, 0.65);
  font-size: 0.95rem;
`;

const MetricValue = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

const SummarySection = styled.div`
  min-height: 11rem;
  margin-bottom: 1rem;
`;

const SummaryBlock = styled.div`
  background: ${(p) =>
    p.$incomplete
      ? "linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%)"
      : p.$pass
      ? "linear-gradient(135deg, #f6ffed 0%, #f0f9eb 100%)"
      : "linear-gradient(135deg, #fffaf5 0%, #fff2e8 100%)"};
  border: 1px solid
    ${(p) => (p.$incomplete ? "#ffe58f" : p.$pass ? "#b7eb8f" : "#ffd8bf")};
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: ${(p) =>
    p.$incomplete
      ? "0 1px 3px rgba(212, 177, 6, 0.08)"
      : p.$pass
      ? "0 1px 3px rgba(82, 196, 26, 0.08)"
      : "0 1px 3px rgba(255, 77, 79, 0.06)"};
  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
    border-radius: 10px;
  }
`;

const SummaryHeading = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const SummaryContent = styled.div`
  animation: summaryFadeIn 0.5s ease-out forwards;
  @keyframes summaryFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const SummaryBlurb = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.8);
`;

const RecommendationText = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.75);
  font-weight: 500;
`;

const RecommendationDisclaimer = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
  padding-top: 0.75rem;
  border-top: 1px solid
    ${(p) =>
      p.$incomplete
        ? "rgba(255, 229, 143, 0.6)"
        : p.$pass
        ? "rgba(183, 235, 143, 0.6)"
        : "rgba(255, 216, 191, 0.7)"};
`;

const PropertyTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PropertyTag = styled.span`
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  background: #f0f0f0;
  color: rgba(0, 0, 0, 0.75);
`;

const DSCR_THRESHOLD = 1.25;

/** Thousands separators in stress-test currency inputs (Ant Design InputNumber formatter/parser). */
function formatCurrencyInputInt(value) {
  if (value === undefined || value === null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return Math.round(n).toLocaleString("en-US");
}

function parseCurrencyInputInt(str) {
  if (str == null || str === "") return null;
  const cleaned = String(str).replace(/,/g, "").trim();
  if (cleaned === "" || cleaned === "-") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Price the analysis used to derive debt service (list price, or ~200× monthly P&I if missing). */
function getReferencePurchasePrice(result) {
  if (!result) return 0;
  const lp = Number(result.listPrice);
  if (lp > 0) return lp;
  const ds = Number(result.debtService) || 0;
  return ds > 0 ? ds * 200 : 0;
}

function isListingUrl(str) {
  if (!str || typeof str !== "string") return false;
  const t = str.trim().toLowerCase();
  return (
    t.startsWith("http://") || t.startsWith("https://") || t.startsWith("www.")
  );
}

export default function DealAnalyzer() {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [strMode, setStrMode] = useState(false);
  const [strNightlyRateOverride, setStrNightlyRateOverride] = useState(null);
  const [scenarioGrossRent, setScenarioGrossRent] = useState(null);
  const [scenarioPurchasePrice, setScenarioPurchasePrice] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (!result || loading || !resultRef.current) return;
    const el = resultRef.current;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [result, loading]);

  useEffect(() => {
    setStrNightlyRateOverride(null);
  }, [result?.requestInput]);

  const strComputed = useMemo(() => {
    const listPrice =
      scenarioPurchasePrice != null && scenarioPurchasePrice > 0
        ? scenarioPurchasePrice
        : result?.listPrice > 0
        ? result.listPrice
        : 0;
    if (!result?.strMode || listPrice <= 0) return null;
    const nightlyRate =
      strNightlyRateOverride ??
      result.estimatedNightlyRate ??
      (result.grossRent * 2.5) / 30;
    const monthlyRent =
      scenarioGrossRent != null
        ? scenarioGrossRent
        : result.grossRent ?? 0;
    const occupancyPercent = result.estimatedOccupancyPercent ?? 70;
    const turnoverCostsAnnual = result.turnoverCostsAnnual ?? 1800;

    const strProjectedAnnualGross =
      nightlyRate * 365 * (occupancyPercent / 100);
    const strProjectedAnnualNet = Math.max(
      0,
      strProjectedAnnualGross - turnoverCostsAnnual
    );
    const strProjectedYieldPercent = (strProjectedAnnualNet / listPrice) * 100;
    const strPremiumMultiple =
      monthlyRent > 0 ? (nightlyRate * 30) / monthlyRent : 2.5;

    return {
      nightlyRate,
      strProjectedAnnualGross,
      strProjectedAnnualNet,
      strProjectedYieldPercent,
      strPremiumMultiple,
    };
  }, [
    result,
    strNightlyRateOverride,
    scenarioPurchasePrice,
    scenarioGrossRent,
  ]);

  const scenarioMetrics = useMemo(() => {
    if (!result || result.dataLimitation) return null;
    const baseDebt = Number(result.debtService) || 0;
    if (baseDebt <= 0) return null;
    if (scenarioGrossRent == null || scenarioPurchasePrice == null) return null;
    const gr = Math.max(0, Number(scenarioGrossRent) || 0);
    const purchase = Math.max(0, Number(scenarioPurchasePrice) || 0);
    const refPrice = getReferencePurchasePrice(result);
    const adjustedDebt =
      refPrice > 0 ? baseDebt * (purchase / refPrice) : baseDebt;
    const dscr =
      adjustedDebt > 0
        ? Math.round((gr / adjustedDebt) * 100) / 100
        : 0;
    const buyBoxPass = dscr >= DSCR_THRESHOLD;
    return {
      grossRent: gr,
      purchase,
      adjustedDebt,
      dscr,
      buyBoxPass,
      refPrice,
    };
  }, [result, scenarioGrossRent, scenarioPurchasePrice]);

  const scenarioSummaryBlurb = useMemo(() => {
    if (!result || !scenarioMetrics) return null;
    const pt = result.propertyType || "Property";
    const bd = result.bedrooms ?? 0;
    const bt = result.bathrooms ?? 0;
    const sq = result.squareFeet;
    const sqPart =
      sq && sq > 0 ? `, ${Number(sq).toLocaleString()} sq ft` : "";
    const { grossRent, dscr, buyBoxPass, purchase } = scenarioMetrics;
    if (buyBoxPass) {
      return `At your $${Number(purchase).toLocaleString()} purchase and $${Number(
        grossRent
      ).toLocaleString()}/mo gross rent, this ${pt.toLowerCase()} (${bd} bed, ${bt} bath${sqPart}) shows a modeled DSCR of ${dscr}, at or above the ${DSCR_THRESHOLD} threshold. Worth a closer look and your own underwriting.`;
    }
    return `At your $${Number(purchase).toLocaleString()} purchase and $${Number(
      grossRent
    ).toLocaleString()}/mo gross rent, this ${pt.toLowerCase()} (${bd} bed, ${bt} bath${sqPart}) has a modeled DSCR of ${dscr}, below the ${DSCR_THRESHOLD} threshold. Consider a lower price, higher rent, or different financing.`;
  }, [result, scenarioMetrics]);

  const scenarioMatchesAnalysisDefaults = useMemo(() => {
    if (!result || !scenarioMetrics || result.dataLimitation) return false;
    const ref = getReferencePurchasePrice(result);
    const gr = Number(result.grossRent) || 0;
    const near = (a, b) => Math.abs(a - b) < 0.01;
    return near(scenarioMetrics.grossRent, gr) && near(scenarioMetrics.purchase, ref) && ref > 0;
  }, [result, scenarioMetrics]);

  const scenarioRecommendation = useMemo(() => {
    if (!scenarioMetrics) return null;
    if (scenarioMetrics.buyBoxPass) {
      if (scenarioMetrics.dscr >= 1.4) {
        return "At your numbers, modeled DSCR is strong. Still confirm loan terms, taxes, insurance, and property condition with your lender and inspector.";
      }
      return "At your numbers, modeled DSCR meets the screening threshold. Proceed with due diligence using your actual quotes.";
    }
    if (scenarioMetrics.dscr >= 1.0) {
      return "At your numbers, DSCR is positive but below the typical buy box. Try reducing purchase price, validating rent, or adjusting down payment and rate.";
    }
    return "At your numbers, modeled rent does not cover debt service with enough cushion. Negotiate a lower price, improve rent assumptions, or pass.";
  }, [scenarioMetrics]);

  const resetScenarioToAnalysis = () => {
    if (!result || result.dataLimitation) return;
    const ds = Number(result.debtService) || 0;
    if (ds <= 0) return;
    setScenarioGrossRent(Number(result.grossRent) || 0);
    setScenarioPurchasePrice(getReferencePurchasePrice(result) || 1);
  };

  const strLtrDisplay = useMemo(() => {
    if (!result?.strMode || result.ltrProjectedYieldPercent == null)
      return null;
    const gross =
      scenarioGrossRent != null
        ? scenarioGrossRent
        : Number(result.grossRent) || 0;
    const annual = gross * 12;
    const listPx =
      scenarioPurchasePrice != null && scenarioPurchasePrice > 0
        ? scenarioPurchasePrice
        : result.listPrice > 0
        ? result.listPrice
        : 0;
    const yieldPct =
      listPx > 0
        ? Math.round((annual / listPx) * 100 * 10) / 10
        : result.ltrProjectedYieldPercent;
    return { annualRevenue: annual, yieldPercent: yieldPct };
  }, [result, scenarioGrossRent, scenarioPurchasePrice]);

  const runAnalysis = async () => {
    const trimmed = (input || "").trim();
    if (!trimmed) {
      message.warning("Enter an address or listing URL");
      return;
    }
    setLoading(true);
    setResult(null);
    setScenarioGrossRent(null);
    setScenarioPurchasePrice(null);
    try {
      const user = getAuth().currentUser;
      const token = user ? await user.getIdToken() : null;
      const base = getApiUrl();
      // Use POST with JSON body so long Zillow URLs don't hit query-string length limits
      const res = await fetch(`${base}/analyzer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ address: trimmed, strMode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Analysis failed (${res.status})`);
      }
      setResult(data);
      const ds = Number(data.debtService) || 0;
      const gr = Number(data.grossRent) || 0;
      const refPurchase =
        data.listPrice > 0 ? data.listPrice : ds > 0 ? ds * 200 : 0;
      if (!data.dataLimitation && ds > 0) {
        setScenarioGrossRent(gr);
        setScenarioPurchasePrice(refPurchase > 0 ? refPurchase : 1);
      }
    } catch (e) {
      message.error(e?.message || "Failed to analyze");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const displayPass =
    !result || result.dataLimitation
      ? false
      : scenarioMetrics
      ? scenarioMetrics.buyBoxPass
      : result.buyBoxPass;

  const displayDscr =
    result && !result.dataLimitation && scenarioMetrics
      ? scenarioMetrics.dscr
      : result?.dscr;

  const summaryUsesLongBlurb = Boolean(
    result && (result.summaryBlurb || scenarioSummaryBlurb)
  );

  return (
    <PageContainer>
      <PageHeader
        title="Deal Analyzer"
        subtitle="Quick DSCR read from Leo on a listing or address"
        icon={RiseOutlined}
      />

      <GuideSection aria-labelledby="deal-analyzer-guide-heading">
        <GuideTitle id="deal-analyzer-guide-heading">Quick tips</GuideTitle>
        <GuideBody>
          <GuideLead>
            Paste a listing URL or street address, then tap{" "}
            <strong>Analyze</strong>. Leo estimates rent, list price, and debt
            service, and compares DSCR to a{" "}
            <strong>{DSCR_THRESHOLD}</strong> screening line. This is a quick
            screen, not a substitute for your lender or an inspection.
          </GuideLead>
          <GuideBullets>
            <li>
              For short-term or vacation rentals, turn on{" "}
              <strong>STR mode</strong> before you run Analyze.
            </li>
            <li>
              After you have results, use{" "}
              <strong>Stress-test your numbers</strong> to try a lower purchase
              price or a different rent.
            </li>
            <li>
              If a listing link does not work, enter the same property as a full
              street address, including city and state.
            </li>
          </GuideBullets>
        </GuideBody>
      </GuideSection>

      <ContentCard>
        <InputRow>
          <InputWrapper>
            <Input
              placeholder="Address or listing URL (Zillow, Realtor.com, Redfin, Apartments.com, etc.)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPressEnter={runAnalysis}
              style={{ background: "transparent" }}
              allowClear
            />
          </InputWrapper>
          <Button
            type="primary"
            icon={<SearchOutlined style={{ filter: "invert(1)" }} />}
            onClick={runAnalysis}
            loading={loading}
          >
            Analyze
          </Button>
        </InputRow>
        <StrToggleRow>
          <span>Short-Term Rental Mode</span>
          <Switch checked={strMode} onChange={setStrMode} />
          <StrToggleHint>
            Prioritize nightly rate & occupancy. Compare LTR vs STR yield.
          </StrToggleHint>
        </StrToggleRow>

        {loading && (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Spin size="large" />
          </div>
        )}
      </ContentCard>

      {loading && <div style={{ minHeight: "11rem" }} aria-hidden="true" />}

      {result && !loading && (
        <ResultScrollAnchor ref={resultRef}>
          <ResultCard>
            <VerdictBanner
              $pass={displayPass}
              $incomplete={!!result.dataLimitation}
            >
              <VerdictText>
                <VerdictTitle
                  $pass={result.dataLimitation ? false : displayPass}
                  $incomplete={!!result.dataLimitation}
                >
                  {result.dataLimitation
                    ? "Unable to analyze"
                    : displayPass
                    ? "Good deal"
                    : "Not a fit"}
                </VerdictTitle>
                <VerdictSub>
                  {result.dataLimitation
                    ? result.dataLimitation
                    : displayPass
                    ? `Modeled DSCR ${
                        displayDscr ?? "n/a"
                      } meets the ${DSCR_THRESHOLD} threshold (using your numbers below). Worth a closer look.`
                    : `Modeled DSCR ${
                        displayDscr ?? "n/a"
                      } is below ${DSCR_THRESHOLD} with your numbers below. Adjust price or rent to stress-test.`}
                </VerdictSub>
              </VerdictText>
            </VerdictBanner>
            {result.requestInput && isListingUrl(result.requestInput) && (
              <ListingLinkSection>
                <ListingLinkLabel>Listing</ListingLinkLabel>
                <ListingLink
                  href={result.requestInput}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {result.requestInput}
                </ListingLink>
              </ListingLinkSection>
            )}
            <MetricsSection>
              <DesktopTwoCol>
                <DesktopCol>
                  <SummarySection>
                    <SummaryBlock
                      $pass={result.dataLimitation ? false : displayPass}
                      $incomplete={!!result.dataLimitation}
                    >
                      <SummaryHeading>Summary</SummaryHeading>
                      <SummaryContent key={result.address ?? "summary"}>
                        <SummaryBlurb>
                          {result.dataLimitation
                            ? result.summaryBlurb
                            : scenarioMatchesAnalysisDefaults &&
                              result.summaryBlurb
                            ? result.summaryBlurb
                            : scenarioSummaryBlurb ||
                              result.summaryBlurb ||
                              (displayPass
                                ? `This property shows a DSCR of ${
                                    displayDscr ?? "n/a"
                                  }, at or above the ${DSCR_THRESHOLD} threshold. Estimated gross rent of $${Number(
                                    result.grossRent ?? 0
                                  ).toLocaleString()}/mo covers debt service with a cushion. Worth a closer look and your own underwriting.`
                                : `This property has a DSCR of ${
                                    displayDscr ?? "n/a"
                                  }, below the ${DSCR_THRESHOLD} threshold. Estimated gross rent of $${Number(
                                    result.grossRent ?? 0
                                  ).toLocaleString()}/mo does not provide enough cushion over debt service. Consider passing or renegotiating.`)}
                        </SummaryBlurb>
                        {(scenarioRecommendation ||
                          result.recommendation) && (
                          <>
                            <RecommendationText>
                              {scenarioMatchesAnalysisDefaults &&
                              result.recommendation
                                ? result.recommendation
                                : scenarioRecommendation ||
                                  result.recommendation}
                            </RecommendationText>
                            <RecommendationDisclaimer
                              $pass={result.dataLimitation ? false : displayPass}
                              $incomplete={!!result.dataLimitation}
                            >
                              Not investment, legal, or financial advice.
                              Estimates and recommendations are for
                              informational purposes only. We do not guarantee
                              that you will obtain the deal, that the deal will
                              perform as estimated, or any particular outcome.
                              You are solely responsible for your own due
                              diligence and investment decisions.
                            </RecommendationDisclaimer>
                          </>
                        )}
                      </SummaryContent>
                    </SummaryBlock>
                  </SummarySection>
                  <DataSourceNote>
                    {result.source === "live_listing"
                      ? "Source: Live Listing"
                      : "Source: AI Estimate"}
                  </DataSourceNote>
                  <FurtherDisclaimer>
                    {result.dataLimitation
                      ? "The listing page could not be fetched. Property details may be inferred from the URL or market data. Enter the address manually for a full analysis."
                      : result.requestInput && isListingUrl(result.requestInput)
                      ? "These estimates are based on the information found on the page at the URL. Further analysis may be required."
                      : "These estimates are based on the information available. Further analysis may be required."}
                  </FurtherDisclaimer>
                </DesktopCol>
                <DesktopCol>
                  {(result.propertyType ||
                    result.bedrooms != null ||
                    result.bathrooms != null ||
                    (result.squareFeet != null && result.squareFeet > 0) ||
                    (result.listPrice != null && result.listPrice > 0)) && (
                    <PropertyTags>
                      {result.listPrice != null && result.listPrice > 0 && (
                        <PropertyTag>
                          ${Number(result.listPrice).toLocaleString()}
                        </PropertyTag>
                      )}
                      {result.propertyType && (
                        <PropertyTag>{result.propertyType}</PropertyTag>
                      )}
                      {result.bedrooms != null && (
                        <PropertyTag>
                          {result.bedrooms} bed
                          {result.bedrooms !== 1 ? "s" : ""}
                        </PropertyTag>
                      )}
                      {result.bathrooms != null && (
                        <PropertyTag>
                          {result.bathrooms} bath
                          {result.bathrooms !== 1 ? "s" : ""}
                        </PropertyTag>
                      )}
                      {result.squareFeet != null && result.squareFeet > 0 && (
                        <PropertyTag>
                          {Number(result.squareFeet).toLocaleString()} sq ft
                        </PropertyTag>
                      )}
                    </PropertyTags>
                  )}
                  {scenarioMetrics && (
                    <ScenarioPanel>
                      <ScenarioHeadRow>
                        <ScenarioPanelTitle>
                          Stress-test your numbers
                        </ScenarioPanelTitle>
                        <ScenarioResetButton
                          type="button"
                          onClick={resetScenarioToAnalysis}
                        >
                          reset values
                        </ScenarioResetButton>
                      </ScenarioHeadRow>
                      <ScenarioPanelHint>
                        <p>
                          Gross rent and purchase price load from the analysis.
                          When you change purchase price, estimated debt service
                          moves in proportion to that price versus the listing
                          reference
                          {result.listPrice > 0
                            ? ` ($${Number(result.listPrice).toLocaleString(
                                "en-US"
                              )})`
                            : scenarioMetrics.refPrice > 0
                            ? ` (inferred about $${Number(
                                scenarioMetrics.refPrice
                              ).toLocaleString("en-US")})`
                            : ""}
                          .
                        </p>
                        <p>
                          Same loan assumptions as in the original estimate.
                        </p>
                      </ScenarioPanelHint>
                      <ScenarioGrid>
                        <ScenarioField>
                          <span className="scenario-field-label">
                            Gross rent (est.), per month
                          </span>
                          <ScenarioMoneyInputGroup>
                            <ScenarioMoneyPrefix aria-hidden>
                              $
                            </ScenarioMoneyPrefix>
                            <ScenarioInput
                              min={0}
                              step={50}
                              bordered={false}
                              value={scenarioGrossRent}
                              formatter={formatCurrencyInputInt}
                              parser={parseCurrencyInputInt}
                              onChange={(v) =>
                                setScenarioGrossRent(
                                  v != null ? Number(v) : 0
                                )
                              }
                              controls={false}
                            />
                          </ScenarioMoneyInputGroup>
                        </ScenarioField>
                        <ScenarioField>
                          <span className="scenario-field-label">
                            Your purchase price
                          </span>
                          <ScenarioMoneyInputGroup>
                            <ScenarioMoneyPrefix aria-hidden>
                              $
                            </ScenarioMoneyPrefix>
                            <ScenarioInput
                              min={1}
                              step={1000}
                              bordered={false}
                              value={scenarioPurchasePrice}
                              formatter={formatCurrencyInputInt}
                              parser={parseCurrencyInputInt}
                              onChange={(v) =>
                                setScenarioPurchasePrice(
                                  v != null && v > 0 ? Number(v) : 1
                                )
                              }
                              controls={false}
                            />
                          </ScenarioMoneyInputGroup>
                        </ScenarioField>
                      </ScenarioGrid>
                    </ScenarioPanel>
                  )}
                  <MetricRow>
                    <MetricLabel>Address</MetricLabel>
                    <MetricValue
                      style={{
                        fontWeight: 500,
                        flex: "1 1 auto",
                        minWidth: 0,
                        marginLeft: "1rem",
                        textAlign: "right",
                      }}
                    >
                      {result.address || "n/a"}
                    </MetricValue>
                  </MetricRow>
                  {result.listPrice != null && result.listPrice > 0 && (
                    <MetricRow>
                      <MetricLabel>List Price</MetricLabel>
                      <MetricValue>
                        ${Number(result.listPrice).toLocaleString()}
                      </MetricValue>
                    </MetricRow>
                  )}
                  {(result.bedrooms != null || result.bathrooms != null) &&
                    !summaryUsesLongBlurb && (
                      <MetricRow>
                        <MetricLabel>Beds / Baths</MetricLabel>
                        <MetricValue>
                          {result.bedrooms != null
                            ? `${result.bedrooms} bed${
                                result.bedrooms !== 1 ? "s" : ""
                              }`
                            : "n/a"}
                          {result.bedrooms != null && result.bathrooms != null
                            ? " / "
                            : ""}
                          {result.bathrooms != null
                            ? `${result.bathrooms} bath${
                                result.bathrooms !== 1 ? "s" : ""
                              }`
                            : "n/a"}
                        </MetricValue>
                      </MetricRow>
                    )}
                  {result.propertyType && !summaryUsesLongBlurb && (
                    <MetricRow>
                      <MetricLabel>Property type</MetricLabel>
                      <MetricValue>{result.propertyType}</MetricValue>
                    </MetricRow>
                  )}
                  <MetricRow>
                    <MetricLabel>Gross Rent (est.)</MetricLabel>
                    <MetricValue>
                      $
                      {Number(
                        scenarioMetrics
                          ? scenarioMetrics.grossRent
                          : result.grossRent ?? 0
                      ).toLocaleString()}
                    </MetricValue>
                  </MetricRow>
                  <MetricRow>
                    <MetricLabel>Debt Service (est.)</MetricLabel>
                    <MetricValue>
                      {result.dataLimitation
                        ? "n/a"
                        : `$${Number(
                            scenarioMetrics
                              ? scenarioMetrics.adjustedDebt
                              : result.debtService ?? 0
                          ).toLocaleString()}`}
                    </MetricValue>
                  </MetricRow>
                  <MetricRow>
                    <MetricLabel>DSCR</MetricLabel>
                    <MetricValue
                      style={{
                        color: result.dataLimitation
                          ? "inherit"
                          : displayPass
                          ? "#52c41a"
                          : "#ff4d4f",
                      }}
                    >
                      {result.dataLimitation
                        ? "n/a"
                        : displayDscr ?? "n/a"}
                    </MetricValue>
                  </MetricRow>
                </DesktopCol>
              </DesktopTwoCol>
              {result.strMode && result.ltrProjectedYieldPercent != null && (
                <StrYieldSection>
                  <StrYieldTitle>
                    <HomeOutlined style={{ fontSize: "1.25rem" }} />
                    <span>
                      <StrYieldTitleMain>
                        Projected Annual Yield:{" "}
                      </StrYieldTitleMain>
                      <StrYieldTitleSub>LTR vs STR</StrYieldTitleSub>
                    </span>
                  </StrYieldTitle>
                  <StrYieldTwoCol>
                    <StrYieldBlock>
                      <StrYieldBlockTitle>
                        Long-Term Rental (LTR)
                      </StrYieldBlockTitle>
                      <StrYieldRow>
                        <StrYieldLabel>Annual revenue</StrYieldLabel>
                        <StrYieldValue>
                          $
                          {Number(
                            strLtrDisplay?.annualRevenue ??
                              result.ltrProjectedAnnualRevenue ??
                              0
                          ).toLocaleString()}
                        </StrYieldValue>
                      </StrYieldRow>
                      <StrYieldRow>
                        <StrYieldLabel>Yield</StrYieldLabel>
                        <StrYieldValue>
                          {strLtrDisplay?.yieldPercent ??
                            result.ltrProjectedYieldPercent ??
                            "n/a"}
                          %
                        </StrYieldValue>
                      </StrYieldRow>
                    </StrYieldBlock>
                    <StrYieldBlock>
                      <StrYieldBlockTitle>
                        Short-Term Rental (STR)
                      </StrYieldBlockTitle>
                      <StrYieldRow>
                        <StrYieldLabel>Nightly rate</StrYieldLabel>
                        <StrYieldValue>
                          <StrNightlyRateInput
                            addonBefore="$"
                            min={1}
                            max={9999}
                            step={5}
                            precision={0}
                            value={
                              strComputed?.nightlyRate ??
                              result.estimatedNightlyRate ??
                              0
                            }
                            onChange={(val) =>
                              setStrNightlyRateOverride(
                                val != null ? Number(val) : null
                              )
                            }
                            controls={false}
                          />
                        </StrYieldValue>
                      </StrYieldRow>
                      <StrYieldRow>
                        <StrYieldLabel>Occupancy</StrYieldLabel>
                        <StrYieldValue>
                          {result.estimatedOccupancyPercent ?? "n/a"}%
                        </StrYieldValue>
                      </StrYieldRow>
                      <StrYieldRow>
                        <StrYieldLabel>Premium vs LTR</StrYieldLabel>
                        <StrYieldValue>
                          {strComputed?.strPremiumMultiple?.toFixed(1) ??
                            result.strPremiumMultiple ??
                            "n/a"}
                          x
                        </StrYieldValue>
                      </StrYieldRow>
                      <StrYieldRow>
                        <StrYieldLabel>
                          Annual net (after turnover)
                        </StrYieldLabel>
                        <StrYieldValue>
                          $
                          {Number(
                            strComputed?.strProjectedAnnualNet ??
                              result.strProjectedAnnualNet ??
                              0
                          ).toLocaleString()}
                        </StrYieldValue>
                      </StrYieldRow>
                      <StrYieldRow>
                        <StrYieldLabel>Yield</StrYieldLabel>
                        <StrYieldValue>
                          {strComputed?.strProjectedYieldPercent?.toFixed(1) ??
                            result.strProjectedYieldPercent ??
                            "n/a"}
                          %
                        </StrYieldValue>
                      </StrYieldRow>
                    </StrYieldBlock>
                  </StrYieldTwoCol>
                  <StrDisclaimer>
                    STR assumes ~{result.turnoverCostsAnnual ?? 1800} in annual
                    turnover (cleaning/supplies). Not investment advice.
                  </StrDisclaimer>
                </StrYieldSection>
              )}
            </MetricsSection>
          </ResultCard>
        </ResultScrollAnchor>
      )}
    </PageContainer>
  );
}
